// importValidation.js - 导入前校验：线路国家匹配 + 地址必须为英文

const CN_REGEX = /[\u4e00-\u9fff\u3400-\u4dbf]/;

export function hasChinese(str) {
	return CN_REGEX.test(String(str === null || str === undefined ? '' : str));
}

export function normalizeCountry(c) {
	return String(c || '').trim().toUpperCase();
}

// 解析日期时间字符串中的"时:分:秒"，无法解析返回 null
export function parseTimeOfDay(v) {
	if (v === null || v === undefined) return null;
	const s = String(v).trim();
	const m = s.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
	if (!m) return null;
	return {
		hour: parseInt(m[1], 10),
		minute: parseInt(m[2], 10),
		second: parseInt(m[3] || '0', 10)
	};
}

// 判断某个日期时间字符串中的日期是否为周日
function isSundayDate(v) {
	const m = String(v || '').match(/(\d{4})-(\d{2})-(\d{2})/);
	if (!m) return false;
	return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10)).getDay() === 0;
}

// 判断某个日期时间字符串中的日期是否为周六
function isSaturdayDate(v) {
	const m = String(v || '').match(/(\d{4})-(\d{2})-(\d{2})/);
	if (!m) return false;
	return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10)).getDay() === 6;
}

// 解析日期时间字符串为毫秒时间戳（UTC，避免时区干扰），无法解析返回 null
function parseDateTimeMs(v) {
	const m = String(v || '').match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?/);
	if (!m) return null;
	return Date.UTC(
		parseInt(m[1], 10),
		parseInt(m[2], 10) - 1,
		parseInt(m[3], 10),
		parseInt(m[4], 10),
		parseInt(m[5], 10),
		parseInt(m[6] || '0', 10)
	);
}

// 线路国家规则：0 美国-美国，1 英国-英国，2 加拿大-加拿大，3 中国-全球（收货地不限）
const LINE_RULES = {
	0: { sender: ['US'], receiver: ['US'] },
	1: { sender: ['GB', 'UK'], receiver: ['GB', 'UK'] },
	2: { sender: ['CA'], receiver: ['CA'] },
	3: { sender: ['CN'], receiver: null }
};

const COUNTRY_MSG = {
	0: '发货地,收货地国家必须为美国',
	1: '发货地,收货地国家必须为英国',
	2: '发货地,收货地国家必须为加拿大',
	3: '发货地国家必须为中国'
};

/**
 * 校验导入记录
 * @param {Array} records 记录列表，每条需含 shipping_address / receiving_address / sender_country / receiver_country / track_end_time
 * @param {number} line 线路：0-3
 * @returns {{ok: boolean, message: string}}
 */
export function validateImportRecords(records, line) {
	const rule = LINE_RULES[line] || LINE_RULES[0];
	let senderCn = 0;
	let receiverCn = 0;
	let countryFail = 0;
	let endTimeFail = 0;
	let startTimeFail = 0;
	let weekendEndFail = 0;
	let sundayStartFail = 0;
	let durationFail = 0;

	(records || []).forEach((r) => {
		if (hasChinese(r.shipping_address)) senderCn++;
		if (hasChinese(r.receiving_address)) receiverCn++;
		const sc = normalizeCountry(r.sender_country);
		const rc = normalizeCountry(r.receiver_country);
		if (!rule.sender.includes(sc) || (rule.receiver && !rule.receiver.includes(rc))) {
			countryFail++;
		}
		// 轨迹结束时间必须在 09:00:00 ~ 17:00:00 之间
		const t = parseTimeOfDay(r.track_end_time);
		const seconds = t ? t.hour * 3600 + t.minute * 60 + t.second : -1;
		if (seconds < 9 * 3600 || seconds > 17 * 3600) {
			endTimeFail++;
		}
		// 轨迹开始时间也必须在 09:00:00 ~ 17:00:00 之间
		const st = parseTimeOfDay(r.track_start_time);
		const startSeconds = st ? st.hour * 3600 + st.minute * 60 + st.second : -1;
		if (startSeconds < 9 * 3600 || startSeconds > 17 * 3600) {
			startTimeFail++;
		}
		// 轨迹开始时间当天不能是周日；轨迹结束时间当天不能是周六或周日
		if (isSundayDate(r.track_start_time)) sundayStartFail++;
		if (isSundayDate(r.track_end_time) || isSaturdayDate(r.track_end_time)) weekendEndFail++;
		// 中国-全球线路：轨迹开始到结束时间必须大于 5 天
		if (line === 3) {
			const startMs = parseDateTimeMs(r.track_start_time);
			const endMs = parseDateTimeMs(r.track_end_time);
			if (startMs !== null && endMs !== null) {
				const hours = (endMs - startMs) / 3600000;
				if (hours <= 5 * 24) {
					durationFail++;
				}
			}
		}
	});

	const addressMsgs = [];
	if (senderCn > 0) addressMsgs.push(senderCn + ' 条发货地址包含中文');
	if (receiverCn > 0) addressMsgs.push(receiverCn + ' 条收货地址包含中文');
	if (addressMsgs.length > 0) {
		return { ok: false, message: addressMsgs.join('，') + '，地址必须为英文，不能包含中文' };
	}

	if (countryFail > 0) {
		return {
			ok: false,
			message: '有 ' + countryFail + ' 条记录的发货/收货国家与线路不符，' + (COUNTRY_MSG[line] || COUNTRY_MSG[0])
		};
	}

	if (endTimeFail > 0) {
		return {
			ok: false,
			message: '有 ' + endTimeFail + ' 条记录的轨迹结束时间不在上午 9 点至下午 17 点之间，请修改后再提交'
		};
	}

	if (startTimeFail > 0) {
		return {
			ok: false,
			message: '有 ' + startTimeFail + ' 条记录的轨迹开始时间不在上午 9 点至下午 17 点之间，请修改后再提交'
		};
	}

	if (sundayStartFail > 0 || weekendEndFail > 0) {
		const parts = [];
		if (sundayStartFail > 0) {
			parts.push('有 ' + sundayStartFail + ' 条记录的轨迹开始时间当天是周日');
		}
		if (weekendEndFail > 0) {
			parts.push('有 ' + weekendEndFail + ' 条记录的轨迹结束时间当天是周六或周日');
		}
		return { ok: false, message: parts.join('，') + '，请修改后再提交' };
	}

	if (durationFail > 0) {
		return {
			ok: false,
			message: '有 ' + durationFail + ' 条记录的轨迹开始到结束时间未超过 5 天（中国-全球线路要求大于 5 天），请修改后再提交'
		};
	}

	return { ok: true, message: '' };
}
